import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { postFetcher, getFetcher, useAuthStore } from "api/swr";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplatePicker } from "../components/TemplatePicker";
import { Template } from "types";
import { displayNameFromFields } from "commons/userDisplayName";

interface CreateSpineTemplateButtonProps {
  onSuccess: () => void; // 创建成功后刷新列表
}

export function CreateSpineTemplateButton({ onSuccess }: CreateSpineTemplateButtonProps) {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [duplicateErrorMsg, setDuplicateErrorMsg] = useState("");

  /** 默认静态类型为列表首项（现为 bgStatic），更多类型可在编辑器内切换 */
  const handleCreateStaticSpine = async () => {
    setIsCreating(true);
    setShowCreateDialog(false);
    try {
      const user = useAuthStore.getState().auth?.user;
      if (!user) {
        toast({
          variant: "destructive",
          title: "创建失败",
          description: "用户信息未找到",
        });
        return;
      }
      const result = await postFetcher("/spine-templates/create", {
        arg: {
          painter: user.id,
          publisher: displayNameFromFields(user),
          type: 'bgStatic',
        },
      });
      onSuccess();
      navigate(`/admin/spine/editor/${result.id}`);
      toast({
        title: "创建成功",
        description: `已创建 Spine 静态模版（默认静态背景），ID: ${result.id}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "创建失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateDirect = async () => {
    setIsCreating(true);
    setShowCreateDialog(false);
    try {
      const user = useAuthStore.getState().auth?.user;
      if (!user) {
        toast({
          variant: "destructive",
          title: "创建失败",
          description: "用户信息未找到",
        });
        return;
      }
      const result = await postFetcher("/spine-templates/create", {
        arg: {
          painter: user.id,
          publisher: displayNameFromFields(user),
          type: 'character',
        },
      });
      onSuccess(); // 刷新列表
      navigate(`/admin/spine/editor/${result.id}`);
      toast({
        title: "创建成功",
        description: `模板 ID: ${result.id}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "创建失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFromTemplate = async (template: Template) => {
    setIsCreating(true);
    setShowTemplatePicker(false);
    try {
      const user = useAuthStore.getState().auth?.user;
      if (!user) {
        toast({
          variant: "destructive",
          title: "创建失败",
          description: "用户信息未找到",
        });
        return;
      }

      // 检查是否已经存在该模板的 Spine 模板
      try {
        const existing = await getFetcher(`/spine-templates/${template.id}`);
        if (existing && existing.state !== -1) {
          setDuplicateErrorMsg(`该模板 (ID: ${template.id}) 已经创建过 Spine 模板了。如果你想重新创建，请在列表页先删除原有的 Spine 模板后再进行此操作。`);
          setShowDuplicateError(true);
          setIsCreating(false);
          return;
        }
      } catch (error: any) {
        // 如果不是 404 错误，则说明请求有问题，需要处理
        if (error.response?.status !== 404) {
          console.error("Check existing template error:", error);
        }
      }

      const result = await postFetcher("/spine-templates/create", {
        arg: {
          painter: user.id,
          publisher: displayNameFromFields(user),
          type: 'character',
          fromTemplateId: template.id,
          fromTemplateShortId: template.shortId,
          fromTemplateName: template.name,
          fromTemplateIntro: template.intro,
        },
      });
      onSuccess(); // 刷新列表
      navigate(`/admin/spine/editor/${result.id}`);
      toast({
        title: "创建成功",
        description: `从模板 "${template.name || template.id}" 创建了新的 Spine 模板 ID: ${result.id}`,
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "创建失败",
        description: error instanceof Error ? error.message : "未知错误",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShowCreateDialog(true)}
        disabled={isCreating}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        {isCreating ? "Creating..." : "Create Spine Template"}
      </Button>

      {/* 创建方式选择弹窗 */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>选择创建方式</DialogTitle>
            <DialogDescription>
              你可以直接创建一个空白模板，或从已有的模板创建
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleCreateDirect}
              disabled={isCreating}
              className="w-full justify-start h-auto py-4"
              variant="outline"
            >
              <div className="flex flex-col items-start gap-1">
                <div className="font-medium">直接创建</div>
                <div className="text-sm text-muted-foreground font-normal">
                  创建一个全新的空白 Spine 模板
                </div>
              </div>
            </Button>
            <Button
              onClick={() => {
                setShowCreateDialog(false);
                setShowTemplatePicker(true);
              }}
              disabled={isCreating}
              className="w-full justify-start h-auto py-4"
              variant="outline"
            >
              <div className="flex flex-col items-start gap-1">
                <div className="font-medium">从已有静态模板创建</div>
                <div className="text-sm text-muted-foreground font-normal">
                  选择一个已有的模板，复制其基本信息创建新模板
                </div>
              </div>
            </Button>
            <Button
              onClick={handleCreateStaticSpine}
              disabled={isCreating}
              className="w-full justify-start h-auto py-4"
              variant="outline"
            >
              <div className="flex flex-col items-start gap-1">
                <div className="font-medium">创建 Spine 静态模版</div>
                <div className="text-sm text-muted-foreground font-normal">
                  spine静态模版, 无资源文件，仅配置基础信息与上传组件
                </div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template 选择器 */}
      <TemplatePicker
        open={showTemplatePicker}
        onOpenChange={setShowTemplatePicker}
        onSelect={handleCreateFromTemplate}
      />
      {/* 重复创建警告弹窗 */}
      <Dialog open={showDuplicateError} onOpenChange={setShowDuplicateError}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">无法创建</DialogTitle>
            <DialogDescription>
              {duplicateErrorMsg}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowDuplicateError(false)}>知道了</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
